import type { LucideIcon } from 'lucide-react';
import { Tabs as R } from 'radix-ui';
import type { ReactNode } from 'react';
import { cx } from '@/lib/cx';
import s from './Tabs.module.css';

export interface TabItem {
  key: string;
  label: ReactNode;
  icon?: LucideIcon;
  badge?: ReactNode;
  disabled?: boolean;
}

/**
 * Tabs whose value is owned by the caller — bind it to a search param (`?tab=`) so every
 * tab is a shareable URL (RULES §UI-3). Scrolls horizontally on phones (IA §4).
 */
export function Tabs({
  value,
  onChange,
  items,
  children,
  label,
  className,
}: {
  value: string;
  onChange: (key: string) => void;
  items: TabItem[];
  children?: ReactNode;
  label: string;
  className?: string;
}) {
  return (
    <R.Root value={value} onValueChange={onChange} className={cx(s.root, className)} activationMode="manual">
      <R.List className={s.list} aria-label={label}>
        {items.map((it) => (
          <R.Trigger key={it.key} value={it.key} disabled={it.disabled} className={s.tab}>
            {it.icon && <it.icon className={s.icon} aria-hidden />}
            <span>{it.label}</span>
            {it.badge != null && <span className={s.badge}>{it.badge}</span>}
          </R.Trigger>
        ))}
      </R.List>
      {children}
    </R.Root>
  );
}

export function TabPanel({ value, children, className }: { value: string; children: ReactNode; className?: string }) {
  return (
    <R.Content value={value} className={cx(s.panel, className)}>
      {children}
    </R.Content>
  );
}
